import { PropsWithChildren, ReactNode } from 'react';
import { FormattedMessage, IntlConfig, IntlProvider, ReactIntlErrorCode } from 'react-intl';

import { logger } from '../../../apps/apps-common/utils/logger';
import { MessageKey, MESSAGES } from './setup';

export const LOCALE_DEFAULT = 'en-US';
export const TRANSLATION_DEFAULT = 'en';
export const LOCALE_FORMATS: IntlConfig['defaultFormats'] = {
  date: {
    default: {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
  },
};

export function isSupportedLanguage(locale: string, supportedLanguages: string[]): boolean {
  return supportedLanguages.includes(locale);
}

export const getMessagesForLocale = (locale: string) => {
  return MESSAGES[locale as keyof typeof MESSAGES];
};

export const getTranslationLanguage = (supportedLanguages: string[]): string => {
  // 0. If no languages are set, return the default translation language.
  if (!window.navigator.language) {
    return TRANSLATION_DEFAULT;
  }

  const locale = new Intl.Locale(window.navigator.language);

  // 1. Check if the full locale (e.g., 'fi-FI') is supported.
  if (isSupportedLanguage(locale.baseName, supportedLanguages)) {
    return locale.baseName;
  }
  // 2. Check if the language part (e.g., 'fi') is supported.
  if (isSupportedLanguage(locale.language, supportedLanguages)) {
    return locale.language;
  }
  // 3. If still not found, return the default translation language.
  return TRANSLATION_DEFAULT;
};

export const isMessageKey = (key: string): key is MessageKey => {
  const messages = Object.keys(getMessagesForLocale('en'));
  return messages.includes(key);
};

export function LocaleProvider({
  children,
  supportedTranslations,
}: PropsWithChildren<{ supportedTranslations: string[] }>) {
  const locale = window.navigator.language ?? LOCALE_DEFAULT;
  const translationLanguage = getTranslationLanguage(supportedTranslations);
  const messages = getMessagesForLocale(translationLanguage);

  return (
    <IntlProvider
      onError={(err) => {
        if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION || err.code === ReactIntlErrorCode.MISSING_DATA) {
          logger.warn('Missing translation or data', {
            code: err.code,
            message: err.message,
            locale,
            translationLanguage,
          });
          return; // Don't break the app if a translation is missing
        }
        throw err;
      }}
      messages={messages}
      locale={locale}
      formats={LOCALE_FORMATS}
      defaultLocale={LOCALE_DEFAULT}
    >
      {children}
    </IntlProvider>
  );
}

/**
 * This is built over React-Intl's <FormattedMessage /> for convenience
 * @example
 * t("guide_infotext_sleep_score", { score })
 */
export function t(id: MessageKey, values?: Record<string, ReactNode | ((chunks: ReactNode) => ReactNode)>) {
  return <FormattedMessage id={id} values={values} />;
}
